<script lang="ts" setup>
import { computed, watch, ref, onMounted } from "vue";
import { SkuDetails } from "./sizePicker.models";
import { useProductPageStore } from "../../stores/productPageStore";
import Icon from "../../components/icon/icon.vue";
import { IconSizes, IconTypes } from "../../components/icon/icon.model";
import { formatSize } from "../../mixins/productSize.mixin";

const props = defineProps<{
  regularSizes: Record<string, any>;
  isSizeGuide?: boolean;
  showInventoryNotification?: boolean;
}>();

const productPageStore = useProductPageStore();
let isRegularSizePicker = true;

// Reactive state for selected values
const selectedRegularSize = ref<string>("");

// Computed properties for store states
const displaySizeNotSelectedError = computed(
  () => productPageStore.displaySizeNotSelectedError,
);

// Function to handle size change
function handleSizeChange(size: string) {
  productPageStore.setSizeSelected(true);
  productPageStore.setSize(size);
  productPageStore.setSkuCode(getSkuCode(size));
  productPageStore.setApptusTicket(getApptusTicket(size));
}

const isOneSize = computed(() => {
  return props.regularSizes?.length === 1 && props.regularSizes[0].length === 1;
});

const regularSizes = computed(() => {
  if (!props.regularSizes) return [];
  let sizes = new Set<string>();

  props.regularSizes?.forEach((skuDetailsArray: SkuDetails[]) => {
    skuDetailsArray.forEach((skuDetails) => {
      sizes.add(skuDetails.regularSize);
    });
  });

  const sizesArray = Array.from(sizes);

  // Check if all sizes are numeric
  const areSizesNumeric = sizesArray.every((size) => !isNaN(Number(size)));

  if (areSizesNumeric) {
    // Sort sizes numerically
    return sizesArray.sort((a, b) => Number(a) - Number(b));
  }

  // Return sizes as is if they are not numeric
  return sizesArray;
});

// Get in-stock regular sizes (different from available sizes)
const inStockRegularSizes = computed(() => {
  if (!props.regularSizes) return [];
  let inStockRegularSizesSet = new Set<string>();

  props.regularSizes?.forEach((skuDetailsArray: SkuDetails[]) => {
    skuDetailsArray.forEach((skuDetails) => {
      if (skuDetails.inventoryCount > 0) {
        inStockRegularSizesSet.add(skuDetails.regularSize);
      }
    });
  });

  return Array.from(inStockRegularSizesSet);
});

const soldOutSizes = computed<SkuDetails[]>(() => {
  return props.regularSizes
    .flat()
    .filter((skuDetails) => skuDetails.inventoryCount === 0);
});

function isWideSize(size: string): boolean {
  return size.length >= 4;
}

function getSkuCode(regularSize: string): string {
  if (!props.regularSizes) return "";
  let skuCode: string | undefined = undefined;

  props.regularSizes?.forEach((skuDetailsArray) => {
    skuDetailsArray.forEach((skuDetails) => {
      if (skuDetails.regularSize === regularSize) {
        skuCode = skuDetails.skuCode;
      }
    });
  });

  return skuCode || "";
}

function getApptusTicket(regularSize: string): string {
  if (!props.regularSizes) return "";
  let ticket: string | undefined = undefined;

  props.regularSizes?.forEach((skuDetailsArray) => {
    skuDetailsArray.forEach((skuDetails) => {
      if (skuDetails.regularSize === regularSize) {
        ticket = skuDetails.ticket;
      }
    });
  });

  return ticket || "";
}

function getInventoryCount(regularSize: string): string {
  if (!props.regularSizes) return "0";
  let inventoryCount = "0";

  props.regularSizes?.forEach((skuDetailsArray) => {
    skuDetailsArray.forEach((skuDetails) => {
      if (skuDetails.regularSize === regularSize) {
        inventoryCount = skuDetails.inventoryCount.toString();
      }
    });
  });

  return inventoryCount;
}

// Function to automatically select a single in-stock option if only one exists
const autoSelectSingleOption = () => {
  if (inStockRegularSizes.value.length === 1) {
    selectedRegularSize.value = inStockRegularSizes.value[0];
    productPageStore.setSizeSelected(true);
    productPageStore.setSkuCode(getSkuCode(inStockRegularSizes.value[0]));
    productPageStore.setApptusTicket(getApptusTicket(inStockRegularSizes.value[0]));
    productPageStore.setSize(inStockRegularSizes.value[0]);
    productPageStore.setInventoryCount(
      getInventoryCount(inStockRegularSizes.value[0]),
    );
    productPageStore.setCheckInventoryStatus(true);
  }
};

// Call the function when the component is mounted
onMounted(() => {
  autoSelectSingleOption();
});

// Watch for changes in `selectedRegularSize` and perform operations
watch(selectedRegularSize, (newSize) => {
  if (!newSize) return;
  // Update the SKU code and inventory count when size is selected
  productPageStore.setSkuCode(getSkuCode(newSize));
  productPageStore.setApptusTicket(getApptusTicket(newSize));
  productPageStore.setSize(newSize);
  productPageStore.setInventoryCount(getInventoryCount(newSize));

  // Trigger inventory status check
  productPageStore.setCheckInventoryStatus(true);
});
</script>
<template>
  <div class="sizepicker">
    <div class="size-tile-group">
      <!-- Size Information -->
      <div class="product__size-information">
        <span id="selectedsize" class="size-tile-group__group--title">
          <span v-if="!selectedRegularSize">{{ $t("chooseSize") }}:</span>
          <!-- Render the selected size when selected -->
          <span v-if="selectedRegularSize">
            {{ $t("chooseSize") }}:
            <strong>{{ formatSize(selectedRegularSize, isRegularSizePicker) }}</strong>
          </span>
        </span>
        <!-- Size Guide Button (Conditional) -->
        <span v-if="isSizeGuide" class="product__size-guide">
          <button
            type="button"
            class="product__size-guide btn btn--small btn--link js-btn _js-vue-component-toggler"
            data-togglewhat="sizeguide"
          >
            {{ $t("sizeGuide") }}
          </button>
        </span>
      </div>

      <fieldset aria-labelledby="selectsizelabel" class="product__size__normal">
        <!-- Sizes -->
        <div
          class="size-tile-group__items"
          :class="[
            {'size-tile-group__items--onesize': isOneSize},
            {'size-tile-group__items--wide': !isOneSize && isWideSize(props.regularSizes?.[0]?.[0]?.regularSize)}
          ]"
        >
        <label
          v-for="size in regularSizes"
          :key="size"
          :class="[
            'size-tile-group__item',
            { 'size-tile-group__item--soldout': !inStockRegularSizes.includes(size) },
          ]"
        >
          <input
            :id="'size_' + size"
            v-model="selectedRegularSize"
            :name='$t("skuParameterCode")'
            type="radio"
            :data-sku="selectedRegularSize ? getSkuCode(selectedRegularSize) : ''"
            :value="size"
            class="size-tile-group__input _js-check-stock"
            :disabled="!inStockRegularSizes.includes(size)"
            @change="handleSizeChange(size)"
          />
          <span class="size-tile-group__text">{{ formatSize(size, isRegularSizePicker) }}</span>
        </label>
        </div>
      </fieldset>
      <!-- Error Message if size not selected -->
      <span v-if="displaySizeNotSelectedError" class="size-tile-group__error">
        {{ $t("selectSizeMessage") }}
      </span>
    </div>
    <!-- Inventory Low Notification -->
    <span v-if="productPageStore.inventoryIsLow" class="size-tile-group__warning">
      {{ productPageStore.inventoryLowMessage }}
    </span>
    <div
      v-if="soldOutSizes.length > 0 && props.showInventoryNotification"
      class="product__inventory-notification _js-vue-inventory-nofitication-btn-container"
    >
      <button
        class="product__inventory-notification btn--link _js-vue-component-toggler _js-vue-inventory-notification-btn"
        data-togglewhat="inventoryNotification"
      >
        <Icon :type="IconTypes.Bell" :size="IconSizes.Tiny" />
        <p>
          {{ $t("inventoryNotificationTrigger") }}
        </p>
      </button>
    </div>
  </div>
</template>
